/**
 * Оболочка: навигация, верхняя панель, полоса контекста анализа, вывод маршрута.
 *
 * Оболочка не владеет запросами экранов и бизнес-состоянием — только каркасом,
 * скип-линком и общим контекстом анализа.
 */

import {
  AlertTriangle,
  BadgeCheck,
  Building2,
  CalendarCheck,
  ClipboardCheck,
  Compass,
  FileText,
  GraduationCap,
  Layers,
  LayoutDashboard,
  ListChecks,
  Map,
  MessageCircleQuestion,
  ShieldAlert,
  Siren,
  Sprout,
  Table2,
} from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAnalysisContext } from "../analysis/context";
import { AnalysisContextBar } from "../analysis/ContextBar";
import { canVerify, roleKind, useAuth, type RoleKind } from "../auth/context";
import { ROUTES } from "../lib/routes";
import type { PrincipalOut } from "../api/client";
import { SignInForm } from "../features/works/MyWorks";
import { defineMessages, type Message } from "../i18n/define";
import { COMMON } from "../i18n/common";
import { useT } from "../i18n/hooks";
import { tr } from "../i18n/runtime";
import { ErrorBoundary } from "./ErrorBoundary";
import { HeaderSearch } from "./HeaderSearch";
import { InfoPopover } from "./InfoPopover";
import { Badge, Button } from "./ui";
import styles from "./shell.module.css";

/**
 * Полоса ширины экрана. Атрибут ставит оболочка, ширину раздаёт CSS.
 *
 * Разделение по вопросу, а не по «важности» экрана: `wide` — там, где человек
 * СРАВНИВАЕТ (карта, реестры, очередь, ход кампаний, картина района), и лишние
 * сантиметры уходят в данные, а не в поля; `reading` — там, где он ЧИТАЕТ
 * связный текст или один объект (обучение, замечания, кабинет хозяйства), и
 * строка длиннее 90 символов перестаёт читаться.
 *
 * Список объявлен адресами целиком, а не префиксами: `/contours` — реестр на
 * весь район, `/contours/1866` — одна карточка, и растягивать её на 27" не за
 * чем. Новый широкий экран добавляется сюда явно; по умолчанию — `reading`.
 */
const WIDE_PATHS: ReadonlySet<string> = new Set([
  "/",
  "/map",
  "/contours",
  "/actors",
  "/verification",
  "/situations",
  "/campaigns",
  "/registry",
]);

export type LayoutWidth = "wide" | "reading";

export function layoutWidthOf(pathname: string): LayoutWidth {
  const path =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return WIDE_PATHS.has(path) ? "wide" : "reading";
}

/**
 * Пункт меню. `visible` — предикат ВИДИМОСТИ, а не права.
 *
 * Спрятанный пункт ничего не защищает: тот же адрес открывается по прямой
 * ссылке, а отказ приходит с сервера. Предикат нужен для другого — не
 * предлагать человеку экран, на котором для его роли нет ни одного его дела.
 * Поэтому он ставится только там, где это правда, и никогда — «на всякий
 * случай»: чтение в пилоте открыто, и анонимному читателю меню не режется.
 */
/**
 * Пункт меню. `visible` — предикат ВИДИМОСТИ, а не права.
 *
 * Спрятанный пункт ничего не защищает: тот же адрес открывается по прямой
 * ссылке, а отказ приходит с сервера. Предикат нужен для другого — не
 * предлагать человеку экран, на котором для его роли нет ни одного его дела.
 * Поэтому он ставится только там, где это правда, и никогда — «на всякий
 * случай»: чтение в пилоте открыто, и анонимному читателю меню не режется.
 */
export interface NavItem {
  to: string;
  /** Короткое существительное. Вопрос живёт в подсказке, а не в пункте. */
  label: string;
  /** На какой вопрос отвечает экран. Уходит в `title`, а не в разметку меню. */
  question: string;
  icon: typeof Compass;
  end: boolean;
  visible?: (principal: PrincipalOut | null) => boolean;
}

export interface NavGroup {
  /** Стабильный ключ раздела: подпись — текст интерфейса, её правят и переводят. */
  key: string;
  /** Раздел назван вопросом человека, а не таблицей хранения (IA-03). */
  title: string;
  question: string;
  items: NavItem[];
}

const nav = defineMessages({
  districtTitle: { id: "common.kit.nav.district.title", defaultMessage: "Район сегодня", description: "Название раздела бокового меню старой оболочки (хоким, менеджер): что сейчас в районе" },
  districtQuestion: { id: "common.kit.nav.district.question", defaultMessage: "Что сейчас происходит в районе на дату среза", description: "Подсказка к разделу «Район сегодня» в боковом меню: вопрос, на который отвечает раздел" },
  overviewLabel: { id: "common.kit.nav.overview.label", defaultMessage: "Картина района", description: "Пункт бокового меню: обзор района; короткое существительное" },
  overviewQuestion: { id: "common.kit.nav.overview.question", defaultMessage: "Что по району сделано, а что нет", description: "Подсказка к пункту «Картина района» (всплывает при наведении)" },
  mapLabel: { id: "common.kit.nav.map.label", defaultMessage: "Карта", description: "Пункт бокового меню: карта района" },
  mapQuestion: { id: "common.kit.nav.map.question", defaultMessage: "Где это на земле и чего карта показать не может", description: "Подсказка к пункту «Карта» в боковом меню" },
  seasonTitle: { id: "common.kit.nav.season.title", defaultMessage: "Сезон", description: "Название раздела бокового меню: ход сезона" },
  seasonQuestion: { id: "common.kit.nav.season.question", defaultMessage: "Как идёт сезон: окна кампаний и агроциклы", description: "Подсказка к разделу «Сезон» в боковом меню" },
  campaignsLabel: { id: "common.kit.nav.campaigns.label", defaultMessage: "Ход кампаний", description: "Пункт бокового меню: как идут кампании" },
  campaignsQuestion: { id: "common.kit.nav.campaigns.question", defaultMessage: "Что кампания собрала и чего ещё ждёт", description: "Подсказка к пункту «Ход кампаний» в боковом меню" },
  agrocycleLabel: { id: "common.kit.nav.agrocycle.label", defaultMessage: "Агроциклы", description: "Пункт бокового меню: агроциклы (культура × сезон)" },
  agrocycleQuestion: { id: "common.kit.nav.agrocycle.question", defaultMessage: "Какие работы открывает агроцикл культуры", description: "Подсказка к пункту «Агроциклы» в боковом меню" },
  checkTitle: { id: "common.kit.nav.check.title", defaultMessage: "Кого проверяем", description: "Название раздела бокового меню: проверка отчётов и сигналы" },
  checkQuestion: { id: "common.kit.nav.check.question", defaultMessage: "О чём заявлено и ещё не разобрано", description: "Подсказка к разделу «Кого проверяем» в боковом меню" },
  verificationLabel: { id: "common.kit.nav.verification.label", defaultMessage: "Проверка", description: "Пункт бокового меню: очередь проверки отчётов" },
  verificationQuestion: { id: "common.kit.nav.verification.question", defaultMessage: "Чей отчёт ждёт вердикта", description: "Подсказка к пункту «Проверка» в боковом меню" },
  situationsLabel: { id: "common.kit.nav.situations.label", defaultMessage: "Ситуации", description: "Пункт бокового меню: сигналы по хозяйствам, которые район разбирает" },
  situationsQuestion: { id: "common.kit.nav.situations.question", defaultMessage: "Где сигнал по хозяйству и что по нему уже решили", description: "Подсказка к пункту «Ситуации» в боковом меню" },
  whoTitle: { id: "common.kit.nav.who.title", defaultMessage: "Кто и где", description: "Название раздела бокового меню: хозяйства и земля" },
  whoQuestion: { id: "common.kit.nav.who.question", defaultMessage: "Кто работает в районе и на какой земле", description: "Подсказка к разделу «Кто и где» в боковом меню" },
  actorsLabel: { id: "common.kit.nav.actors.label", defaultMessage: "Хозяйства", description: "Пункт бокового меню: реестр хозяйств" },
  actorsQuestion: { id: "common.kit.nav.actors.question", defaultMessage: "Кто ведёт хозяйство и сколько у него земли", description: "Подсказка к пункту «Хозяйства» в боковом меню" },
  contoursLabel: { id: "common.kit.nav.contours.label", defaultMessage: "Контуры", description: "Пункт бокового меню: реестр контуров (участков земли)" },
  contoursQuestion: { id: "common.kit.nav.contours.question", defaultMessage: "Какой контур чей и что на нём", description: "Подсказка к пункту «Контуры» в боковом меню" },
  servicesTitle: { id: "common.kit.nav.services.title", defaultMessage: "Службы", description: "Название раздела бокового меню: данные служб (карантин и др.)" },
  servicesQuestion: { id: "common.kit.nav.services.question", defaultMessage: "Что говорят службы о той же земле", description: "Подсказка к разделу «Службы» в боковом меню" },
  quarantineLabel: { id: "common.kit.nav.quarantine.label", defaultMessage: "Карантин", description: "Пункт бокового меню: карантин растений" },
  quarantineQuestion: { id: "common.kit.nav.quarantine.question", defaultMessage: "Где действуют ограничения и как идёт профилактика", description: "Подсказка к пункту «Карантин» в боковом меню" },
  realTitle: { id: "common.kit.nav.real.title", defaultMessage: "Что здесь настоящее", description: "Название раздела бокового меню: какие данные настоящие, а какие нет" },
  realQuestion: { id: "common.kit.nav.real.question", defaultMessage: "Как собран канон и что в него не вошло", description: "Подсказка к разделу «Что здесь настоящее» в боковом меню" },
  registryLabel: { id: "common.kit.nav.registry.label", defaultMessage: "Что вошло и что нет", description: "Пункт бокового меню: сводка реестра — что вошло в канон, что нет" },
  registryQuestion: { id: "common.kit.nav.registry.question", defaultMessage: "Как собран канон, что в него не вошло и сходится ли сверка", description: "Подсказка к пункту «Что вошло и что нет» в боковом меню" },
  qualityLabel: { id: "common.kit.nav.quality.label", defaultMessage: "Замечания", description: "Пункт бокового меню: замечания к качеству данных" },
  qualityQuestion: { id: "common.kit.nav.quality.question", defaultMessage: "Что в данных не сходится и насколько это мешает", description: "Подсказка к пункту «Замечания» в боковом меню" },
  cabinetTitle: { id: "common.kit.nav.cabinet.title", defaultMessage: "Кабинет хозяйства", description: "Название раздела бокового меню: рабочее место фермера / ввод за хозяйство" },
  cabinetQuestion: { id: "common.kit.nav.cabinet.question", defaultMessage: "Что от хозяйства ждут и о чём оно отчиталось", description: "Подсказка к разделу «Кабинет хозяйства» в боковом меню" },
  todayLabel: { id: "common.kit.nav.today.label", defaultMessage: "Сегодня", description: "Пункт бокового меню кабинета хозяйства: что делать сегодня" },
  todayQuestion: { id: "common.kit.nav.today.question", defaultMessage: "Что от меня ждут прямо сейчас и к какому сроку", description: "Подсказка к пункту «Сегодня» в кабинете хозяйства (фермер)" },
  fieldsLabel: { id: "common.kit.nav.fields.label", defaultMessage: "Мои поля", description: "Пункт бокового меню кабинета хозяйства: свои поля (контуры)" },
  fieldsQuestion: { id: "common.kit.nav.fields.question", defaultMessage: "Какая земля за мной и что на ней", description: "Подсказка к пункту «Мои поля» (фермер)" },
  historyLabel: { id: "common.kit.nav.history.label", defaultMessage: "Мои отчёты", description: "Пункт бокового меню кабинета хозяйства: свои отчёты" },
  historyQuestion: { id: "common.kit.nav.history.question", defaultMessage: "О чём я отчитался и что сказала проверка", description: "Подсказка к пункту «Мои отчёты» (фермер)" },
  farmWorksLabel: { id: "common.kit.nav.farmWorks.label", defaultMessage: "Ввод за хозяйство", description: "Пункт бокового меню (менеджер района): ввести факт работы за хозяйство" },
  farmWorksQuestion: { id: "common.kit.nav.farmWorks.question", defaultMessage: "Отчитаться за хозяйство от имени района", description: "Подсказка к пункту «Ввод за хозяйство» (менеджер района)" },
  answersTitle: { id: "common.kit.nav.answers.title", defaultMessage: "Ответы", description: "Название раздела бокового меню: вопросы к данным" },
  answersQuestion: { id: "common.kit.nav.answers.question", defaultMessage: "Что можно спросить у корпуса и что уже спрашивали", description: "Подсказка к разделу «Ответы» в боковом меню; «корпус» — набор принятых данных" },
  askLabel: { id: "common.kit.nav.ask.label", defaultMessage: "Спросить", description: "Пункт бокового меню: задать вопрос данным" },
  askQuestion: { id: "common.kit.nav.ask.question", defaultMessage: "Чем ответить на вопрос и что об этом записано в журнале", description: "Подсказка к пункту «Спросить» в боковом меню" },
  learnTitle: { id: "common.kit.nav.learn.title", defaultMessage: "Обучение", description: "Название раздела бокового меню: как пользоваться системой" },
  learnQuestion: { id: "common.kit.nav.learn.question", defaultMessage: "Какой экран отвечает на ваш вопрос и где границы версии", description: "Подсказка к разделу «Обучение» в боковом меню" },
  guideLabel: { id: "common.kit.nav.guide.label", defaultMessage: "Карта экранов", description: "Пункт бокового меню: путеводитель по экранам" },
  guideQuestion: { id: "common.kit.nav.guide.question", defaultMessage: "Какой экран отвечает на какой вопрос", description: "Подсказка к пункту «Карта экранов» в боковом меню" },
});

const shellText = defineMessages({
  mainNav: { id: "common.kit.shell.mainNav", defaultMessage: "Основная навигация", description: "aria-label бокового меню (для экранных дикторов)" },
  topbarTitle: { id: "common.kit.shell.topbarTitle", defaultMessage: "Канонический реестр района", description: "Заголовок верхней панели старой оболочки" },
  topbarNote: { id: "common.kit.shell.topbarNote", defaultMessage: "Чтение открыто · запись требует входа", description: "Пояснение в верхней панели: смотреть может любой, вносить данные — только после входа" },
  liveContext: {
    id: "common.kit.shell.liveContext",
    defaultMessage: "Контекст анализа: {asOf}, {demo, select, yes {с демо-данными} other {без демо-данных}}",
    description: "Невидимая подпись для экранных дикторов (aria-live): на какую дату смотрим и учтены ли демо-данные; {asOf} — дата среза или фраза «как знаем сейчас»",
  },
  asOfNow: { id: "common.kit.shell.asOfNow", defaultMessage: "как знаем сейчас", description: "Дата среза не выбрана: показано последнее известное состояние; встаёт внутрь фразы" },
  checkingSignIn: { id: "common.kit.shell.checkingSignIn", defaultMessage: "проверяем вход…", description: "Короткая пометка в верхней панели, пока проверяется, кто вошёл" },
  districtBadge: { id: "common.kit.shell.districtBadge", defaultMessage: "район", description: "Бейдж рядом с именем: у аккаунта область роли — весь район; коротко" },
  districtBadgeTitle: { id: "common.kit.shell.districtBadgeTitle", defaultMessage: "Область роли — весь район: записи по чужому хозяйству будут помечены «от имени»", description: "Подсказка к бейджу «район» в верхней панели" },
  farmBadgeTitle: { id: "common.kit.shell.farmBadgeTitle", defaultMessage: "Хозяйство, за которое аккаунт действует как за своё", description: "Подсказка к бейджу с именем хозяйства в верхней панели" },
  farmFallback: { id: "common.kit.shell.farmFallback", defaultMessage: "хозяйство #{id}", description: "Имя хозяйства в бейдже, когда названия нет; {id} — номер хозяйства" },
});

/** Пункт меню: подписи берутся из каталога при чтении, смена языка их подхватывает. */
function navItem(
  to: string,
  label: Message,
  question: Message,
  icon: typeof Compass,
  end: boolean,
  visible?: NavItem["visible"],
): NavItem {
  return {
    to,
    get label() {
      return tr(label as never);
    },
    get question() {
      return tr(question as never);
    },
    icon,
    end,
    visible,
  };
}

function navGroup(key: string, title: Message, question: Message, items: NavItem[]): NavGroup {
  return {
    key,
    get title() {
      return tr(title as never);
    },
    get question() {
      return tr(question as never);
    },
    items,
  };
}

/** Ключ раздела, с которого начинает читать меню аккаунт хозяйства. */
const FARM_GROUP = "cabinet";

/**
 * Разделы названы по вопросу, а не по слою хранения (IA-03, бэклог §2.2).
 *
 * «Территория», «Субъекты», «Данные и доказательства» — это имена таблиц:
 * человек ищет в них не то, что хочет узнать, а то, где это, по его догадке,
 * лежит. Раздел теперь называется вопросом, пункт остаётся коротким
 * существительным, а сам вопрос — в подсказке, чтобы меню не превратилось в
 * страницу текста.
 *
 * Состав пунктов и адреса не менялись: это переименование разделов, а не
 * новая карта продукта. Новые адреса из §2.5 сюда не заводятся, пока за ними
 * нет экрана.
 *
 * Подписи — геттеры над каталогом сообщений: потребители (`Guide`, тесты)
 * читают строки как раньше, а язык применяется в момент чтения.
 */
export const NAV: NavGroup[] = [
  navGroup("district", nav.districtTitle, nav.districtQuestion, [
    // Первый экран отвечает «что сделано, а что нет», и это другой вопрос,
    // чем «как собран канон» у сводки реестра. Их разделение намеренно.
    navItem("/", nav.overviewLabel, nav.overviewQuestion, Compass, true),
    navItem(ROUTES.map, nav.mapLabel, nav.mapQuestion, Map, false),
  ]),
  navGroup("season", nav.seasonTitle, nav.seasonQuestion, [
    navItem("/campaigns", nav.campaignsLabel, nav.campaignsQuestion, ClipboardCheck, false),
    // Агроцикл стоит рядом с кампаниями и не внутри них: кампания порождает
    // работы на полях, а агроцикл ими не управляет — он открывает работы. Это
    // разные механизмы, и вкладкой одного другой быть не может.
    navItem("/agrocycle", nav.agrocycleLabel, nav.agrocycleQuestion, Sprout, false),
  ]),
  navGroup("check", nav.checkTitle, nav.checkQuestion, [
    // Третий вход в модель работ, а не вкладка кампаний: вопрос
    // проверяющего другой — не «что сделано», а «о чём заявлено».
    //
    // Очередь проверки — рабочее место того, кто выносит вердикт. Скрыт
    // пункт только у того, у кого своих дел в ней нет ни одного: аккаунт
    // хозяйства без права проверки собственную работу не подтверждает.
    // Проверяющему с областью «свои хозяйства» она нужна — по области он
    // такой же `farm`, и решает здесь `can_verify` с сервера, а не роль.
    // Аноним пункт видит: чтение открыто, и очередь читаема.
    navItem("/verification", nav.verificationLabel, nav.verificationQuestion, BadgeCheck, false, (principal) =>
      roleKind(principal) !== "farm" || canVerify(principal),
    ),
    // Рядом с очередью проверки и НЕ её вкладкой: проверка судит отчёт
    // («сходится ли заявленное»), ситуация называет совпадение фактов по
    // хозяйству и хранит то, как человек им распорядился. Пункт виден всем,
    // кроме аккаунта хозяйства: ситуация — это взгляд района НА хозяйство,
    // и закрывать её про себя нельзя.
    navItem(ROUTES.situations, nav.situationsLabel, nav.situationsQuestion, Siren, false, (principal) =>
      roleKind(principal) !== "farm",
    ),
  ]),
  navGroup("who", nav.whoTitle, nav.whoQuestion, [
    navItem("/actors", nav.actorsLabel, nav.actorsQuestion, Building2, false),
    navItem("/contours", nav.contoursLabel, nav.contoursQuestion, Table2, false),
  ]),
  // Раздел службы, а не территории: карантин смотрит на ту же землю, но со
  // своей стороны — профилактика и статусы. Рядом с ним встанут ирригация и
  // техника, когда придут их срезы.
  navGroup("services", nav.servicesTitle, nav.servicesQuestion, [
    navItem("/quarantine", nav.quarantineLabel, nav.quarantineQuestion, ShieldAlert, false),
  ]),
  navGroup("real", nav.realTitle, nav.realQuestion, [
    // Пункт назван так же, как экран: «Сводка реестра» в меню и «Что вошло
    // и что нет» в заголовке читались бы как два разных места.
    navItem("/registry", nav.registryLabel, nav.registryQuestion, LayoutDashboard, false),
    navItem("/data-quality", nav.qualityLabel, nav.qualityQuestion, AlertTriangle, false),
  ]),
  // Работы — не «раздел территории» и не «раздел службы»: это то, что от
  // человека ЖДУТ. Вход снизу, со стороны своего хозяйства; вход сверху,
  // со стороны района, живёт в «Сезоне».
  navGroup(FARM_GROUP, nav.cabinetTitle, nav.cabinetQuestion, [
    // Три вопроса хозяйства — три пункта, а не один экран с вкладками:
    // ссылку «мои поля» иначе некуда было бы дать.
    navItem(ROUTES.myToday, nav.todayLabel, nav.todayQuestion, CalendarCheck, false),
    navItem(ROUTES.myFields, nav.fieldsLabel, nav.fieldsQuestion, Layers, false),
    navItem(ROUTES.myHistory, nav.historyLabel, nav.historyQuestion, FileText, false),
    // Ввод ЗА чужое хозяйство — работа диспетчера, а не кабинет своего.
    // Аккаунту хозяйства пункт не показывается: называть чужие поля
    // «моими» нельзя, а свои у него уже есть выше.
    navItem(ROUTES.farmWorks, nav.farmWorksLabel, nav.farmWorksQuestion, ListChecks, false, (principal) =>
      roleKind(principal) !== "farm",
    ),
  ]),
  // Раздел назван исходом, а не средством: человек приходит сюда за
  // ОТВЕТОМ, и называть раздел «AI» значило бы обещать ему собеседника.
  navGroup("answers", nav.answersTitle, nav.answersQuestion, [
    navItem(ROUTES.analytics, nav.askLabel, nav.askQuestion, MessageCircleQuestion, false),
  ]),
  // Обучение идёт по живым данным среза, а не по картинкам: экран, на
  // котором «всё понятно», проверяется только тем, что человек нашёл на нём
  // ответ на свой вопрос.
  navGroup("learn", nav.learnTitle, nav.learnQuestion, [
    navItem(ROUTES.guide, nav.guideLabel, nav.guideQuestion, GraduationCap, false),
  ]),
];

/**
 * Порядок групп по роли.
 *
 * Меняется порядок, а не состав: аккаунт хозяйства первым видит то, что от
 * него ждут, — район остаётся на месте, ниже. Роль выбирает, с чего человек
 * начинает читать меню, и не решает, что ему доступно.
 */
function orderGroups(groups: NavGroup[], kind: RoleKind): NavGroup[] {
  if (kind !== "farm") return groups;
  const office = groups.filter((group) => group.key === FARM_GROUP);
  return [...office, ...groups.filter((group) => !office.includes(group))];
}

export function AppShell() {
  const context = useAnalysisContext();
  const { principal } = useAuth();
  const location = useLocation();
  const width = layoutWidthOf(location.pathname);
  const t = useT();

  const groups = useMemo(() => {
    const kind = roleKind(principal);
    return orderGroups(NAV, kind)
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => item.visible?.(principal) ?? true,
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [principal]);

  return (
    <div className={styles.shell}>
      <a className="skip-link" href="#content">
        {t(COMMON.skipToContent)}
      </a>

      <nav className={styles.sidebar} aria-label={t(shellText.mainNav)}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden />
          Agro AI
        </div>
        {groups.map((group) => (
          <div className={styles.navGroup} key={group.key}>
            <div className={styles.navGroupTitle} title={group.question}>
              {group.title}
              {/* Вопрос раздела остаётся и в `title` — как быстрый повтор для
                  мыши, — но открывается он теперь и с клавиатуры, и на
                  сенсорном экране. Пустой `title` на обёртке гасит нативную
                  подсказку родителя над самим знаком вопроса. */}
              <span title="">
                <InfoPopover label={group.title} heading={group.title}>
                  {group.question}
                </InfoPopover>
              </span>
            </div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                // Только контекст анализа: фильтры одного экрана не должны
                // приезжать на другой и там переосмысливаться.
                to={{ pathname: item.to, search: context.linkSearch() }}
                end={item.end}
                // Вопрос экрана — в подсказке: пункт остаётся коротким
                // существительным, а меню не превращается в текст.
                title={item.question}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
                }
              >
                <item.icon size={18} strokeWidth={1.75} aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Полоса ширины объявляется на общем контейнере: по ней выравниваются
          и шапка, и полоса контекста, и содержимое. */}
      <div className={styles.main} data-layout={width}>
        <header className={styles.topbar}>
          <span className={styles.topbarTitle}>{t(shellText.topbarTitle)}</span>
          <span className={styles.topbarNote}>
            {t(shellText.topbarNote)}
          </span>
          {/* Быстрый переход стоит в шапке, а не на обзоре (IA-16): вопрос
              «а что там у …» задаётся с любого экрана, и возвращаться ради
              него на обзор значит терять разобранный контекст. */}
          <HeaderSearch />
          <SessionControl />
        </header>

        <AnalysisContextBar />

        {/* Ширину раздаёт CSS, а решение о полосе принимает маршрут: экран
            сравнения и экран чтения не могут жить в одной колонке. */}
        <main className={styles.content} id="content" data-layout={width}>
          {/* Граница ошибки стоит вокруг экрана, а не вокруг всего дерева:
              навигация, полоса контекста и цепочка уровней обязаны пережить
              падение одного экрана — иначе уйти с него будет некуда. Ключ
              сброса — адрес: перешли на другой экран, граница снова рабочая. */}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <span className="visually-hidden" aria-live="polite">
        {t(shellText.liveContext, {
          asOf: context.asOf ?? t(shellText.asOfNow),
          demo: context.includeDemo ? "yes" : "no",
        })}
      </span>
    </div>
  );
}

/**
 * Кто вошёл — и выход. Стоит в шапке, потому что «от чьего имени я сейчас
 * действую» — это вопрос обо ВСЁМ экране, а не об одной кнопке.
 *
 * Роли показываются рядом с именем намеренно: человек с районной областью
 * обязан видеть, что его следующая запись будет записью «от имени».
 */
function SessionControl() {
  const { principal, loading, signOut } = useAuth();
  const t = useT();

  if (loading) {
    return <span className={styles.topbarNote}>{t(shellText.checkingSignIn)}</span>;
  }
  if (!principal || !principal.is_authenticated) {
    return <SignInForm />;
  }
  return (
    <span className={styles.session}>
      <span className={styles.sessionName}>{principal.username}</span>
      {principal.district_scope ? (
        <Badge tone="attention" title={t(shellText.districtBadgeTitle)}>
          {t(shellText.districtBadge)}
        </Badge>
      ) : null}
      {principal.actors.map((actor) => (
        <Badge key={actor.id} title={t(shellText.farmBadgeTitle)}>
          {actor.name ?? t(shellText.farmFallback, { id: actor.id })}
        </Badge>
      ))}
      <Button onClick={() => void signOut()}>{t(COMMON.signOut)}</Button>
    </span>
  );
}

export function PageHeader({
  title,
  purpose,
  breadcrumb,
  actions,
}: {
  title: string;
  purpose?: ReactNode;
  breadcrumb?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={styles.pageHeader}>
      {breadcrumb ? <div className={styles.breadcrumb}>{breadcrumb}</div> : null}
      <div className={styles.sectionHead}>
        <h1>{title}</h1>
        {actions ? <div className={styles.pageActions}>{actions}</div> : null}
      </div>
      {purpose ? <p className={styles.pagePurpose}>{purpose}</p> : null}
    </div>
  );
}

export const layout = styles;
